import {
  METADATA_PAGE_SIZE,
  METADATA_PREFETCH_BATCHES,
  RECORD_FETCH_BATCH_ROWS,
  RECORD_PAGE_SIZE,
  RECORD_PREFETCH_BATCHES,
} from './constants'
import type { RowRange } from './globalSearchSections'

/**
 * The metadata page a result tab needs for `range`: from the page-aligned row
 * above it through METADATA_PREFETCH_BATCHES pages below it. Null when every
 * row in it is already loaded.
 */
export function planMetadataWindow(
  range: RowRange,
  total: number,
  isLoaded: (index: number) => boolean
): { offset: number; limit: number } | null {
  if (total <= 0) return null

  const offset = Math.max(0, Math.floor(range.start / METADATA_PAGE_SIZE) * METADATA_PAGE_SIZE)
  const desiredEnd = Math.min(
    total,
    Math.ceil((range.end + METADATA_PAGE_SIZE * METADATA_PREFETCH_BATCHES) / METADATA_PAGE_SIZE) *
      METADATA_PAGE_SIZE
  )
  const limit = Math.max(METADATA_PAGE_SIZE, desiredEnd - offset)
  for (let index = offset; index < desiredEnd; index += 1) {
    if (!isLoaded(index)) return { offset, limit }
  }
  return null
}

/**
 * Offsets of the RECORD_FETCH_BATCH_ROWS-row batches covering `range` plus
 * RECORD_PREFETCH_BATCHES pages below it that still miss rows.
 */
export function planRecordBatches(
  range: RowRange,
  total: number,
  isLoaded: (index: number) => boolean
): number[] {
  if (total <= 0) return []

  const coverageStart = Math.max(0, range.start)
  const coverageEnd = Math.min(total, range.end + RECORD_PAGE_SIZE * RECORD_PREFETCH_BATCHES)
  if (coverageEnd <= coverageStart) return []

  const offsets: number[] = []
  const firstBatch = Math.floor(coverageStart / RECORD_FETCH_BATCH_ROWS)
  const lastBatch = Math.floor((coverageEnd - 1) / RECORD_FETCH_BATCH_ROWS)
  for (let batch = firstBatch; batch <= lastBatch; batch += 1) {
    const offset = batch * RECORD_FETCH_BATCH_ROWS
    const batchEnd = Math.min(total, offset + RECORD_FETCH_BATCH_ROWS)
    for (let index = offset; index < batchEnd; index += 1) {
      if (!isLoaded(index)) {
        offsets.push(offset)
        break
      }
    }
  }
  return offsets
}
